import { FC, memo, useMemo } from "react";
import { useNavigate } from "react-router";
import { observer } from "mobx-react";

import { IMessage } from "../../../services";
import ChatUserMessage from "./chat-message/ChatUserMessage";
import ChatServerMessage from "./chat-message/ChatServerMessage";
import ContextWarningMessageBox from "./chat-message/ContextWarningMessageBox";
import UnansweredMessageNotice from "./chat-message/UnansweredMessageNotice";
import { IMessageMetadata } from "../../../types/chat";
import { useChatStore } from "../../../hooks/useChatStore";

interface ChatMessageProps {
  message: IMessage;
  isLastMessage?: boolean;
  userAvatar?: string;
  isCrossUserMessage?: boolean
}

const ChatMessage: FC<ChatMessageProps> = observer(
  ({ message, isLastMessage, userAvatar, isCrossUserMessage }) => {
    const navigate = useNavigate();
    const chatStore = useChatStore();

    // Extract contextWarning from metadata for consistency (fallback to top-level for backward compatibility)
    // Handle both contextWarning and contentWarning (typo fix) for backward compatibility
    const metadata = useMemo(() => {
      const currentMetadata = message.metadata as IMessageMetadata | undefined;

      return {
        ...currentMetadata || {},
        files: chatStore.currentChatFiles?.files,
      };
    }, [message.metadata, chatStore.currentChatFiles]);

    const contextWarning =
      message.contextWarning ||
      (metadata
        ? metadata.contextWarning || metadata.contentWarning || false
        : false);

    const onNewChat = () => {
      chatStore.clearWarningMessage();
      chatStore.askQuestion("Starting a new chat", { skip: true });
      chatStore.reset();
      navigate("/");
    };

    const onSummarize = () => {
      chatStore.clearWarningMessage();
      chatStore.askQuestion("Summarize response", { summarize: true });
    };

    if (contextWarning) {
      return (
        <ContextWarningMessageBox
          text={
            typeof message.data === "string"
              ? message.data
              : JSON.stringify(message.data)
          }
          onSummarize={onSummarize}
          onNewChat={onNewChat}
          showButtons={isLastMessage && !chatStore.isInputDisabled}
        />
      );
    }

    if (message.sender === "me") {
      return (
        <>
          <ChatUserMessage
            message={message}
            // Per-message (REV-2177): a non-empty author_avatar means the backend
            // determined this message was sent by someone other than the viewer
            // (e.g. the original sender in a cloned/shared chat).
            isCrossUserMessage={chatStore.isReadOnly || Boolean(message.author_avatar)}
            userAvatar={userAvatar}
          />
          {chatStore.retryableMessageId === String(message.id) ? (
            <UnansweredMessageNotice
              onRetry={() => void chatStore.retryUnansweredMessage()}
            />
          ) : chatStore.stillSendingId === String(message.id) ? (
            <UnansweredMessageNotice />
          ) : null}
        </>
      );
    }

    return (
      <ChatServerMessage
        message={{...message, metadata}}
        isCrossUserMessage={chatStore.isReadOnly || isCrossUserMessage}
      />
    );
  },
);

export default memo(ChatMessage);
